import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PremiumDialog from "@/components/PremiumDialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { ChartLineUp, Lock, Sparkle } from "@phosphor-icons/react";
import usePageMeta from "@/lib/usePageMeta";

const COLORS = ["#E27D60","#85A389","#C25E43","#6E8C73","#E58F6C"];

export default function Analytics() {
  usePageMeta({ title: "Journey Analytics", description: "See the shape of your Wingwomen circle, match distribution, top shared conditions, and life-stage breakdown." });
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPremium, setOpenPremium] = useState(false);

  const load = async () => {
    if (!user?.premium) { setLoading(false); return; }
    try {
      const r = await api.get("/analytics");
      setData(r.data);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [user?.premium]);

  if (!user?.premium) {
    return (
      <div className="max-w-3xl">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3"><span className="rule" />Journey analytics</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight">See the shape of your circle.</h1>
        <div className="mt-10 bg-white border border-border rounded-2xl p-10 text-center shadow-card">
          <Lock size={38} weight="duotone" className="text-coral-700 mx-auto" />
          <div className="font-display text-2xl mt-4">Premium unlocks this view.</div>
          <p className="mt-3 text-ink/70 max-w-md mx-auto text-sm leading-relaxed">
            Analytics shows how your journey compares, score distribution across peers,
            top shared conditions and treatments, life-stage breakdown.
          </p>
          <button onClick={() => setOpenPremium(true)} data-testid="analytics-upgrade-cta"
            className="mt-6 bg-coral text-white px-6 py-3 rounded-full font-medium hover:bg-coral-600 transition-colors inline-flex items-center gap-2">
            <Sparkle size={16} weight="duotone" /> Upgrade to Premium
          </button>
        </div>
        <PremiumDialog open={openPremium} onClose={() => setOpenPremium(false)} onUpgraded={load} />
      </div>
    );
  }

  if (loading || !data) return <div className="text-muted-foreground">Loading analytics…</div>;

  return (
    <div className="max-w-5xl">
      <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3"><span className="rule" />Journey analytics</div>
      <h1 className="font-display text-4xl sm:text-5xl leading-tight flex items-center gap-3">
        <ChartLineUp size={38} weight="duotone" className="text-coral-700" />
        Your circle, by the numbers.
      </h1>

      <div className="mt-10 grid md:grid-cols-4 gap-4 stagger">
        <Stat label="Peers matched" value={data.match_count} />
        <Stat label="Avg similarity" value={`${data.avg_score}%`} />
        <Stat label="Top match" value={`${data.top_score}%`} />
        <Stat label="Your posts" value={data.posts_created} />
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <Card title="Score distribution">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.score_buckets}>
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#6B6B6B" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip cursor={{ fill: "#F3EFEA" }} contentStyle={{ border: "1px solid #E8E2D9", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" radius={[8,8,0,0]}>
                {data.score_buckets.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Peer life stages">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.peer_stages} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#2B2B2B" }} axisLine={false} tickLine={false} width={90} />
              <Tooltip contentStyle={{ border: "1px solid #E8E2D9", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" radius={[0,8,8,0]}>
                {data.peer_stages.map((_, i) => <Cell key={i} fill={COLORS[(i+1) % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Most shared conditions">
          <div className="space-y-3">
            {data.top_conditions.length === 0 && <div className="text-sm text-muted-foreground">Not enough data yet.</div>}
            {data.top_conditions.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3">
                <div className="text-sm font-display w-32 truncate">{c.name}</div>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-coral" style={{ width: `${Math.min(100, c.count * 20)}%` }} />
                </div>
                <div className="text-xs text-muted-foreground font-mono w-6 text-right">{c.count}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Most shared treatments">
          <div className="space-y-3">
            {data.top_treatments.length === 0 && <div className="text-sm text-muted-foreground">Not enough data yet.</div>}
            {data.top_treatments.map((c) => (
              <div key={c.name} className="flex items-center gap-3">
                <div className="text-sm font-display w-32 truncate">{c.name}</div>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-sage" style={{ width: `${Math.min(100, c.count * 20)}%` }} />
                </div>
                <div className="text-xs text-muted-foreground font-mono w-6 text-right">{c.count}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-white border border-border rounded-2xl p-5 shadow-soft">
    <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground">{label}</div>
    <div className="font-display text-3xl mt-1">{value}</div>
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white border border-border rounded-2xl p-5 shadow-soft">
    <div className="text-xs tracking-[0.2em] uppercase text-sage-600 mb-4">{title}</div>
    {children}
  </div>
);
